"use client";

/**
 * L'aperçu des documents de /outils, tel qu'ils sortiront de l'imprimante.
 *
 * Il lit la même forme en blocs que le PDF, composée par les modules de
 * `lib/tools` et dessinée par lib/tools/documentPdf.js. Les quatre types de
 * blocs sont documentés là-bas.
 */

/**
 * Le document tel qu'il sortira de l'imprimante.
 *
 * La feuille reste blanche en thème sombre : ce n'est pas une surface
 * d'interface, c'est du papier. Le lecteur doit voir ce qu'il télécharge, pas
 * une version repeinte de son document.
 */
export function ApercuDocument({ document: doc, signature }) {
    if (!doc) return null;

    return (
        <div className="rounded-xl border border-gray-200/70 dark:border-white/10 bg-white p-6 sm:p-8 text-[#05060F]">
            <div className="font-bold text-base leading-snug">{doc.titre}</div>

            {doc.blocs.map((bloc, i) => {
                const cle = `${bloc.type}-${i}`;

                if (bloc.type === "lignes") {
                    return (
                        <div key={cle} className="mt-5 space-y-1">
                            {bloc.lignes.map((ligne) => (
                                <p key={ligne} className="text-sm leading-7 mb-0">
                                    {ligne}
                                </p>
                            ))}
                        </div>
                    );
                }

                if (bloc.type === "entete") {
                    /* Deux colonnes côte à côte dès que la place le permet,
                       empilées sinon : sur un téléphone, une adresse coupée en
                       deux colonnes de quarante millimètres se lit moins bien
                       que deux blocs l'un sous l'autre. */
                    return (
                        <div key={cle} className="mt-5 grid sm:grid-cols-2 gap-5">
                            {bloc.colonnes.map((colonne, rang) => (
                                <div key={rang}>
                                    {colonne.libelle && (
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                                            {colonne.libelle}
                                        </div>
                                    )}
                                    {colonne.lignes.map((ligne, i) => (
                                        <p
                                            key={i}
                                            className="text-[13px] leading-6 mb-0"
                                        >
                                            {ligne || "\u00a0"}
                                        </p>
                                    ))}
                                </div>
                            ))}
                        </div>
                    );
                }

                if (bloc.type === "signatures") {
                    return (
                        <div key={cle} className="mt-6 grid sm:grid-cols-2 gap-5">
                            {bloc.colonnes.map((pave) => (
                                <div key={pave.libelle || pave.nom}>
                                    {pave.libelle && (
                                        <p className="text-[11px] leading-5 text-gray-500 mb-1.5">
                                            {pave.libelle}
                                        </p>
                                    )}
                                    <div className="h-16 rounded border border-gray-300 flex items-center px-2">
                                        {signature && pave.paraphe !== false && (
                                            // eslint-disable-next-line @next/next/no-img-element
                                            <img
                                                src={signature}
                                                alt=""
                                                className="h-12 object-contain object-left"
                                            />
                                        )}
                                    </div>
                                    {pave.nom && (
                                        <p className="text-[11px] leading-5 text-gray-500 mt-1.5 mb-0">
                                            {pave.nom}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                }

                if (bloc.type === "signature") {
                    return (
                        <div key={cle} className="mt-6">
                            {bloc.libelle && (
                                <p className="text-sm leading-7 mb-0">{bloc.libelle}</p>
                            )}
                            {signature && bloc.paraphe !== false ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                    src={signature}
                                    alt=""
                                    className="h-14 mt-1 object-contain object-left"
                                />
                            ) : (
                                /* `paraphe: false` laisse le pavé vide : c'est
                                   celui que le client remplira à la main. */
                                <div className="h-14" />
                            )}
                            {bloc.nom && <p className="text-sm leading-7 mb-0">{bloc.nom}</p>}
                        </div>
                    );
                }

                if (bloc.type === "tableau") {
                    return (
                        <div key={cle} className="mt-5 overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr>
                                        {bloc.entetes.map((entete) => (
                                            <th
                                                key={entete}
                                                className="border border-gray-300 px-3 py-2 text-left font-semibold align-top"
                                            >
                                                {entete}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {bloc.lignes.map((ligne) => (
                                        <tr key={ligne.join("|")}>
                                            {ligne.map((cellule, c) => (
                                                <td
                                                    key={`${ligne[0]}-${c}`}
                                                    className="border border-gray-300 px-3 py-2 align-top"
                                                >
                                                    {cellule}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {bloc.total && (
                                        <tr>
                                            {bloc.total.map((cellule, c) => (
                                                <td
                                                    key={`total-${c}`}
                                                    className="border border-gray-300 px-3 py-2 font-semibold"
                                                >
                                                    {cellule}
                                                </td>
                                            ))}
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    );
                }

                if (bloc.type === "intertitre") {
                    return (
                        <p key={cle} className="text-sm font-bold leading-7 mt-7 mb-0">
                            {bloc.texte}
                        </p>
                    );
                }

                if (bloc.type === "liste") {
                    return (
                        <ul key={cle} className="mt-3 space-y-1 pl-0">
                            {bloc.points.map((point) => (
                                <li key={point} className="flex gap-2.5 text-sm leading-7">
                                    <span aria-hidden="true">•</span>
                                    <span>{point}</span>
                                </li>
                            ))}
                        </ul>
                    );
                }

                if (bloc.type === "note") {
                    return (
                        <p key={cle} className="text-xs leading-6 mt-6 mb-0">
                            {bloc.texte}
                        </p>
                    );
                }

                return (
                    <p key={cle} className="text-sm leading-7 mt-5 mb-0">
                        {bloc.texte}
                    </p>
                );
            })}
        </div>
    );
}
